import {
  Content,
  Header,
  Item,
  Root,
  Trigger,
  type AccordionContentProps,
  type AccordionItemProps,
} from '@radix-ui/react-accordion';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
} from 'react';
import { cn } from '../lib/cn';
import { Button, type ButtonSize, type ButtonVariant } from './button';

const AccordionItem = forwardRef<
  ComponentRef<typeof Item>,
  AccordionItemProps & { className?: string }
>(({ children, className, ...props }, forwardedRef) => (
  <Item
    ref={forwardedRef}
    className={cn('overflow-hidden', className)}
    {...props}
  >
    {children}
  </Item>
));

const AccordionTrigger = forwardRef<
  ComponentRef<'div'>,
  ComponentPropsWithoutRef<typeof Trigger> & {
    triggerButton?: {
      variant?: ButtonVariant;
      size?: ButtonSize;
      showOnLeft?: boolean;
      className?: string;
    };
  }
>(
  (
    {
      children,
      className,
      triggerButton = {
        variant: 'secondary',
        size: 'small',
        showOnLeft: false,
      },
      ...props
    },
    forwardedRef
  ) => (
    <Header className="flex">
      <Trigger asChild {...props}>
        <span
          role="button"
          tabIndex={0}
          ref={forwardedRef}
          className={cn(
            'group flex min-w-0 flex-1 cursor-default items-center outline-none select-none',
            triggerButton.showOnLeft ? 'gap-x-3' : 'justify-between',
            className
          )}
        >
          {children}
          <Button
            iconed
            leftIconName="chevron-down"
            size={triggerButton.size}
            variant={triggerButton.variant}
            className={cn(
              'transition-transform duration-500 group-data-[state=open]:rotate-180',
              triggerButton.className
            )}
          />
        </span>
      </Trigger>
    </Header>
  )
);

const AccordionContent = forwardRef<
  ComponentRef<typeof Content>,
  AccordionContentProps & { className?: string }
>(({ children, className, ...props }, forwardedRef) => (
  <Content
    ref={forwardedRef}
    className={
      'data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down overflow-hidden'
    }
    {...props}
  >
    <div className={className}>{children}</div>
  </Content>
));

export const Accordion = {
  Root,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
};
